import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, UserPlus, PencilLine, CheckCircle2, Mail, Loader2 } from "lucide-react";
import { http } from "@/lib/api";
import { EMPTY_FORM } from "@/lib/reteForm";
import ReteForm from "@/components/ReteForm";

const errText = (ex, fallback) => (typeof ex.response?.data?.detail === "string" ? ex.response.data.detail : fallback);
const box = "rounded-3xl border border-viola/10 dark:border-cream/10 bg-white/60 dark:bg-viola/20 p-5 sm:p-8 max-w-xl";

function ModeCard({ active, onClick, icon, title, text, testId }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active} data-testid={testId}
      className={`text-left rounded-3xl border-2 p-6 transition-colors ${active ? "border-fucsia bg-fucsia/5" : "border-viola/15 hover:border-viola/40 dark:border-cream/15"}`}>
      <span className="grid h-10 w-10 place-items-center rounded-full bg-viola text-cream dark:bg-giallo dark:text-viola">{icon}</span>
      <span className="mt-4 block font-display text-xl">{title}</span>
      <span className="mt-1 block text-sm text-viola/75 dark:text-cream/75">{text}</span>
    </button>
  );
}

function RequestLink() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | busy | sent
  const [error, setError] = useState("");
  const go = async (e) => {
    e.preventDefault();
    setState("busy");
    setError("");
    try {
      await http.post("/rete/edit-link", { email: email.trim() });
      setState("sent");
    } catch (ex) {
      setError(errText(ex, "Invio non riuscito, riprova tra poco."));
      setState("idle");
    }
  };
  if (state === "sent") {
    return (
      <div className={box} role="status" data-testid="edit-link-sent">
        <Mail className="text-fucsia" size={26} aria-hidden />
        <h2 className="mt-3 font-display text-2xl">Controlla la tua email</h2>
        <p className="mt-2 text-sm text-viola/80 dark:text-cream/80">Se <strong>{email}</strong> è l'indirizzo di una scheda nella Rete, ti è appena arrivato un link personale per modificarla. Vale 2 ore e si usa una sola volta. Guarda anche nello spam.</p>
        <button type="button" onClick={() => setState("idle")} className="mt-4 text-sm font-semibold text-fucsia underline" data-testid="edit-link-retry">Non è arrivato? Riprova</button>
      </div>
    );
  }
  return (
    <form onSubmit={go} className={box} data-testid="edit-link-form">
      <h2 className="font-display text-2xl">Ricevi il tuo link personale</h2>
      <p className="mt-2 text-sm text-viola/75 dark:text-cream/75">Per proteggere la tua scheda, la modifica si apre solo da un link che mandiamo all'email con cui sei nella Rete. Così nessun altro può cambiarla.</p>
      <label htmlFor="el-email" className="mt-5 block font-semibold">La tua e-mail</label>
      <input id="el-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email"
        className="mt-2 w-full rounded-xl border border-viola/20 bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-fucsia dark:bg-viola/40 dark:border-cream/20" data-testid="edit-link-email" />
      {error && <p className="mt-3 text-sm font-semibold text-red-700 dark:text-red-300" role="alert" data-testid="edit-link-error">{error}</p>}
      <button type="submit" disabled={state === "busy"} className="mt-5 inline-flex items-center gap-2 rounded-full bg-viola text-cream px-6 py-3 font-semibold disabled:opacity-60 dark:bg-giallo dark:text-viola" data-testid="edit-link-submit">
        <Mail size={16} aria-hidden /> {state === "busy" ? "Invio…" : "Mandami il link"}
      </button>
      <p className="mt-4 text-xs text-viola/70 dark:text-cream/70">Hai cambiato email? Scrivi a <a className="underline" href="mailto:dania.piras90@gmail.com">dania.piras90@gmail.com</a>.</p>
    </form>
  );
}

function TokenEditor({ token, onDone }) {
  const [matches, setMatches] = useState(null);
  const [found, setFound] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    http.post("/rete/edit-session", { token })
      .then((r) => { setMatches(r.data.matches); if (r.data.matches.length === 1) setFound(r.data.matches[0]); })
      .catch((ex) => setError(errText(ex, "Il link non è valido.")));
  }, [token]);
  if (error) {
    return (
      <div className="space-y-6">
        <p className="max-w-xl rounded-2xl border-2 border-red-600 bg-red-50 p-4 text-red-800 dark:bg-red-950/40 dark:text-red-200" role="alert" data-testid="edit-token-error">{error}</p>
        <RequestLink />
      </div>
    );
  }
  if (!matches) return <p className="inline-flex items-center gap-2 text-sm" role="status"><Loader2 size={16} className="animate-spin" aria-hidden /> Apro la tua scheda…</p>;
  if (!found) {
    return (
      <div className={box} data-testid="edit-token-matches">
        <p className="font-semibold">Hai più schede nella Rete: quale vuoi aggiornare?</p>
        <div className="mt-3 space-y-2">
          {matches.map((m) => (
            <button key={m.id} type="button" onClick={() => setFound(m)} className="w-full text-left rounded-xl border border-viola/20 px-4 py-3 hover:border-fucsia dark:border-cream/20" data-testid={`edit-match-${m.id}`}>
              {m.data.nome} {m.data.cognome} · <strong>{m.qualifica}</strong>
            </button>
          ))}
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="mb-6 rounded-2xl bg-giallo/30 border border-giallo p-4 text-sm" role="status" data-testid="update-banner">
        Ciao {found.data.nome}! Qui sotto trovi le tue risposte attuali. <strong>Novità:</strong> nelle sezioni Friendly e Formed c'è una voce che prima mancava, <strong>uso di sostanze / riduzione del danno</strong>, evidenziata in giallo. Le modifiche saranno pubblicate subito.
      </div>
      <ReteForm key={found.id} initial={{ ...EMPTY_FORM, ...found.data }} mode="update" targetId={found.id} token={token} onDone={() => onDone(found.id)} />
    </>
  );
}

export default function ReteIscrizione() {
  const [params, setParams] = useSearchParams();
  const token = params.get("token");
  const mode = params.get("modo") === "aggiorna" || token ? "update" : "new";
  const [done, setDone] = useState(null);

  const setMode = (m) => { setDone(null); setParams(m === "update" ? { modo: "aggiorna" } : {}, { replace: true }); };
  const finish = (recordId) => {
    setDone({ recordId });
    window.scrollTo({ top: 0, behavior: "smooth" });
    setTimeout(() => document.getElementById("success-title")?.focus({ preventScroll: true }), 50);
  };

  return (
    <div className="container-editorial py-16 lg:py-24 dark:text-cream" data-testid="page-rete-iscrizione">
      <Link to="/rete" className="inline-flex items-center gap-1.5 text-sm font-semibold text-viola/70 hover:text-fucsia dark:text-cream/70" data-testid="back-to-rete">
        <ArrowLeft size={14} aria-hidden /> Torna alla Rete
      </Link>
      <div className="mt-6 max-w-3xl">
        <span className="pill">Rete GSRD · per professionistə</span>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">Entra nella Rete o aggiorna la tua scheda</h1>
        <div className="mt-6 space-y-3 text-viola/80 dark:text-cream/80">
          <p>Questo modulo raccoglie i contatti di professionistə GSRD, per permettere a chi cerca una figura di cura di avere un range di proposte adeguatamente formate tra cui scegliere.</p>
          <p>Compilandolo, lə professionista <strong>autodichiara</strong> le proprie competenze prendendosi la responsabilità di quanto affermato. Per ragioni legislative il modulo è riservato alle <strong>professioni normate e iscritte a un Albo</strong>.</p>
          <p>Le nuove richieste vengono lette e approvate prima della pubblicazione. Ti chiedo di compilare con la massima precisione possibile.</p>
        </div>
      </div>

      {done ? (
        <div className="mt-10 max-w-2xl rounded-3xl border-2 border-green-700/40 bg-green-50 dark:bg-green-950/30 p-6 sm:p-8" role="status" data-testid="rete-form-success">
          <CheckCircle2 className="text-green-700 dark:text-green-300" size={28} aria-hidden />
          <h2 id="success-title" tabIndex={-1} className="mt-3 font-display text-2xl focus:outline-none">{mode === "update" ? "Scheda aggiornata!" : "Grazie, richiesta inviata!"}</h2>
          <p className="mt-2 text-viola/80 dark:text-cream/80">{mode === "update" ? "Le modifiche sono già visibili nella Rete. Se hai più di una scheda, puoi riaprire lo stesso link dall'email per aggiornare anche le altre." : "La tua scheda verrà controllata e, appena approvata, riceverai un'email con il link alla tua pagina."}</p>
          <div className="mt-5 flex flex-wrap gap-3">
            {mode === "update" && done.recordId && <Link to={`/rete/scheda/${done.recordId}`} className="inline-flex rounded-full bg-fucsia text-white px-6 py-3 font-semibold" data-testid="success-view-scheda">Vedi la tua scheda</Link>}
            <Link to="/rete" className="inline-flex rounded-full bg-viola text-cream px-6 py-3 font-semibold dark:bg-giallo dark:text-viola">Torna alla Rete</Link>
          </div>
        </div>
      ) : (
        <>
          {!token && (
            <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-3xl">
              <ModeCard active={mode === "new"} onClick={() => setMode("new")} icon={<UserPlus size={18} aria-hidden />} title="Voglio entrare nella Rete" text="Compila il modulo per inserire una nuova scheda." testId="mode-new" />
              <ModeCard active={mode === "update"} onClick={() => setMode("update")} icon={<PencilLine size={18} aria-hidden />} title="Modifica la mia scheda esistente" text="Aggiorna i tuoi dati e aggiungi la nuova voce su sostanze e riduzione del danno." testId="mode-update" />
            </div>
          )}
          <div className="mt-10 max-w-4xl">
            {mode === "new" && <ReteForm key="new" initial={EMPTY_FORM} mode="new" onDone={() => finish(null)} />}
            {mode === "update" && !token && <RequestLink />}
            {mode === "update" && token && <TokenEditor token={token} onDone={finish} />}
          </div>
        </>
      )}
    </div>
  );
}
